interface JwtPayload {
  sub: string; // user ID
  email: string;
  name: string;
  role: string;
  jti: string;
  iat: number;
  exp: number;
  iss: string;
  aud: string;
}

interface DecodedUser {
  id: number;
  email: string;
  name: string;
  role: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

export class JwtUtils {
  /**
   * Decode a JWT token without verification (client-side only)
   * Note: This is for reading claims only, not for security validation
   */
  static decodeToken(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as JwtPayload;
    } catch (error) {
      return null;
    }
  }

  /**
   * Extract user information from JWT token
   */
  static getUserFromToken(token: string): DecodedUser | null {
    const payload = this.decodeToken(token);
    if (!payload) {
      return null;
    }

    return {
      id: parseInt(payload.sub),
      email: payload.email,
      name: payload.name,
      role: payload.role,
      isAdmin: payload.role === 'Admin' || payload.role === 'SuperAdmin',
      isSuperAdmin: payload.role === 'SuperAdmin',
    };
  }

  /**
   * Check if token is expired
   */
  static isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload) {
      return true;
    }

    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  }

  /**
   * Get role from token
   */
  static getRoleFromToken(token: string): string | null {
    const payload = this.decodeToken(token);
    return payload?.role || null;
  }

  /**
   * Check if user is admin from token
   */
  static isAdminFromToken(token: string): boolean {
    const role = this.getRoleFromToken(token);
    return role === 'Admin' || role === 'SuperAdmin';
  }

  /**
   * Check if user is super admin from token
   */
  static isSuperAdminFromToken(token: string): boolean {
    const role = this.getRoleFromToken(token);
    return role === 'SuperAdmin';
  }

  /**
   * Get user ID from token
   */
  static getUserIdFromToken(token: string): number | null {
    const payload = this.decodeToken(token);
    return payload ? parseInt(payload.sub) : null;
  }
}

export default JwtUtils;
